import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import EnhancedBriefingModal from './EnhancedBriefingModal';
import LogoSvg from './LogoSvg';

const Archive = ({ authUser, onLogout }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Helper function to check if briefing should show "Has errors"
  const shouldShowHasErrors = (hasError, error) => {
    if (!hasError || !authUser || !authUser.is_admin) return false;
    
    // If there's no error text, don't show "Has errors"
    if (!error || !error.trim()) return false;
    
    // Filter out "Insufficient articles", "Fewer than", and "all categories" notes
    const filteredError = error
      .split('\n')
      .filter(line => 
        !line.includes('Insufficient articles') && 
        !line.includes('Fewer than') &&
        !line.includes('all categories')
      )
      .join('\n')
      .trim();
    
    return filteredError.length > 0;
  };
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showBriefingModal, setShowBriefingModal] = useState(false);
  const [selectedBriefing, setSelectedBriefing] = useState(null);
  const [briefingData, setBriefingData] = useState(null);
  const [singleStory, setSingleStory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load all briefings by default
    loadAllBriefings();
  }, []);

  const loadAllBriefings = async () => {
    setLoading(true);
    try {
      // Use a broad search to get all briefings
      const res = await axios.get('/api/search_briefings?q=');
      setSearchResults(res.data.results || []);
    } catch (err) {
      console.log('Failed to load briefings:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminClick = () => {
    navigate('/admin');
  };

  const handleLogout = () => {
    onLogout();
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      // If search is empty, load all briefings
      loadAllBriefings();
      return;
    }
    
    setSearchLoading(true);
    try {
      const res = await axios.get(`/api/search_briefings?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchResults(res.data.results || []);
    } catch (err) {
      console.log('Search failed:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleBriefingClick = async (briefingId) => {
    setSelectedBriefing(briefingId);
    setBriefingData(null);
    setShowBriefingModal(true);
    
    try {
      const res = await axios.get(`/api/briefings/${briefingId}`);
      setBriefingData(res.data);
    } catch (err) {
      console.log('Failed to fetch briefing:', err);
      setBriefingData({ error: 'Failed to load briefing data' });
    }
  };

  const closeBriefingModal = () => {
    setShowBriefingModal(false);
    setSelectedBriefing(null);
    setBriefingData(null);
    setSingleStory(null);
  };

  const handleStoryClick = (story, storyType = 'insight') => {
    // Set up single story view for archive page (public view)
    setSingleStory({ story, type: storyType });
    setBriefingData(null);
    setSelectedBriefing(null);
    setShowBriefingModal(true);
    
    // Since this is a single story view, we don't need to fetch full briefing data
    // The story already contains all the data we need
    setBriefingData({ 
      ai_title: story.article?.title || story.ai_takeaway_title || 'Industry Story',
      error: null
    });
  };

  return (
    <div className="full-height-container" style={{ backgroundColor: '#ffffff', color: '#333333' }}>
      {/* Header */}
      <header className="site-header site-header-style">
        <div className="container">
          <nav className="navbar navbar-expand-lg navbar-light py-3">
            <div className="d-flex justify-content-between align-items-center w-100">
              <LogoSvg 
                alt="Arkansas Electric Cooperatives, Inc." 
                className="logo-large"
              />
              <div className="d-flex align-items-center gap-3">
                <nav className="d-none d-lg-flex">
                  <ul className="navbar-nav me-4" style={{ display: 'none' }}>
                    <li className="nav-item mx-2">
                      <a className="nav-link text-dark" href="#home">Home</a>
                    </li>
                    <li className="nav-item mx-2">
                      <a className="nav-link text-dark" href="#about">About Us</a>
                    </li>
                    <li className="nav-item mx-2">
                      <a className="nav-link text-dark" href="#services">Services</a>
                    </li>
                    <li className="nav-item mx-2">
                      <a className="nav-link text-dark" href="#news">News</a>
                    </li>
                    <li className="nav-item mx-2">
                      <a className="nav-link text-dark" href="#contact">Contact</a>
                    </li>
                  </ul>
                </nav>
                
                {/* Archive button for all users */}
                <button 
                  className="btn btn-outline-primary px-4 py-2"
                  onClick={() => navigate('/')}
                >
                  Home
                </button>
                
                {/* Show Admin Panel button only for admin users */}
                {authUser && authUser.is_admin && (
                  <button 
                    className="btn btn-success px-4 py-2 btn-success-custom"
                    onClick={handleAdminClick}
                  >
                    Admin Panel
                  </button>
                )}
                
                {/* Logout button for all authenticated users */}
                {authUser && (
                  <button 
                    className="btn btn-outline-secondary px-4 py-2"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                )}
              </div>
            </div>
          </nav>
        </div>
      </header>

      {/* Archive Section */}
      <section className="section-primary">
        <div className="container">
          <div className="text-center mb-5">
            <h1 className="section-title">
              📁 The Archive
            </h1>
            <p className="section-subtitle">
              Browse and search through all our news briefings and industry insights
            </p>
          </div>
          
          {/* Search Section */}
          <div className="row justify-content-center mb-5">
            <div className="col-lg-8">
              <div className="d-flex">
                <input 
                  type="text" 
                  className="form-control form-control-lg me-3 search-input"
                  placeholder="Search briefings by keywords, topics, or dates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <button 
                  className="btn btn-success btn-lg px-4 btn-search"
                  onClick={handleSearch}
                  disabled={searchLoading}
                >
                  {searchLoading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                      Searching...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-search me-2"></i>Search
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="row justify-content-center">
            <div className="col-lg-10">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-search fa-3x text-muted mb-3"></i>
                  <h4>No briefings found</h4>
                  <p className="text-muted">
                    {searchQuery.trim() 
                      ? `No results found for "${searchQuery}". Try different keywords.`
                      : 'No briefings are available at this time.'
                    }
                  </p>
                </div>
              ) : (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3>
                      {searchQuery.trim() 
                        ? `Search Results (${searchResults.length} briefing${searchResults.length !== 1 ? 's' : ''})`
                        : `All Briefings (${searchResults.length} briefing${searchResults.length !== 1 ? 's' : ''})`
                      }
                    </h3>
                    {searchQuery.trim() && (
                      <button 
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          setSearchQuery('');
                          loadAllBriefings();
                        }}
                      >
                        Show All
                      </button>
                    )}
                  </div>
                  
                  <div className="list-group">
                    {searchResults.map((result) => (
                      <button
                        key={result.id}
                        className="list-group-item list-group-item-action search-results-clickable"
                        onClick={() => handleBriefingClick(result.id)}
                      >
                        <div className="d-flex w-100 justify-content-between">
                          <h5 className="mb-1">{result.ai_title || `Briefing #${result.id}`}</h5>
                          <small className="text-muted">
                            {result.generated_at ? new Date(result.generated_at).toLocaleDateString() : 'No date'}
                          </small>
                        </div>
                        <div className="d-flex gap-3 mt-2">
                          <small className="text-muted">
                            <i className="fas fa-lightbulb me-1"></i>
                            {result.insights_count} insights
                          </small>
                          <small className="text-muted">
                            <i className="fas fa-calendar-week me-1"></i>
                            {result.week_stories_count} weekly
                          </small>
                          <small className="text-muted">
                            <i className="fas fa-calendar-day me-1"></i>
                            {result.day_stories_count} daily
                          </small>
                          {shouldShowHasErrors(result.has_error, result.error) && (
                            <small className="text-warning">
                              <i className="fas fa-exclamation-triangle me-1"></i>
                              Has errors
                            </small>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Briefing Modal */}
      {showBriefingModal && (
        <EnhancedBriefingModal
          show={showBriefingModal}
          briefingData={briefingData}
          summaryId={selectedBriefing}
          singleStory={singleStory}
          isPublic={true}
          authUser={authUser}
          onClose={closeBriefingModal}
        />
      )}
    </div>
  );
};

export default Archive;